import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import CompanyOptions from "modules/companies/CompanyOptions";
import { CustomGetProposalQueryData } from "hooks/proposals/useCustomGetProposal";
import useCustomUpdateProposal from "hooks/proposals/useCustomUpdateProposal";
import { headerCase } from "change-case";
import { Company, ProposalSource } from "generated/graphql";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import {
  updateProposalSchema,
  getDefaultValues,
  UpdateProposalFormValues,
  CLOSE_CONFIDENCE_OPTIONS,
} from "DDD/action-objects/ProposalUpdate";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import LabeledValue from "./LabeledValue";
import { draftColors } from "./draftDesignTokens";

type ClientInfoCardProps = {
  proposal: NonNullable<CustomGetProposalQueryData>;
};

const editDropdownSx = {
  "& .MuiInput-root, & .MuiOutlinedInput-root, & .MuiInputBase-root": {
    fontSize: "12px",
    fontWeight: 400,
    color: draftColors.textPrimary,
    borderRadius: "8px",
    border: `1px solid ${draftColors.border} !important`,
    padding: "6px 8px !important",
    minHeight: "36px",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    "&::before, &::after": { display: "none" },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  },
  "& .MuiInputBase-input": { padding: "0 !important", fontSize: "12px" },
  "& .MuiSelect-select": { padding: "0 28px 0 0 !important", fontSize: "12px" },
  "& .MuiSelect-icon": { right: "8px" },
  "& .MuiAutocomplete-endAdornment": { right: "8px !important" },
  "& .MuiFormLabel-root": { display: "none" },
  "& .MuiFormControl-root": {
    marginTop: "0 !important",
    paddingTop: "0 !important",
    width: "100%",
  },
  "& input[type='date']": {
    minHeight: "22px",
    padding: "0 !important",
    fontSize: "12px",
    "&::-webkit-calendar-picker-indicator": { cursor: "pointer" },
  },
} as const;

const editFieldLabelSx = { mb: "2px" } as const;

export default function ClientInfoCard({ proposal }: ClientInfoCardProps) {
  const [editable, setEditable] = useState(false);
  const [triggerRefetch, setTriggerRefetch] = useState(false);
  const [updateProposal, { isSuccess }] = useCustomUpdateProposal();

  const defaults = useMemo(
    () =>
      getDefaultValues({
        id: proposal?.id,
        companyId: proposal.company?.id,
        contactId: proposal?.contactId,
        source: proposal?.source,
        projectedCloseDate: proposal?.projectedCloseDate ?? "",
        closeConfidence: proposal?.closeConfidence ?? null,
      }),
    [proposal]
  );

  const { control, handleSubmit, watch, reset } = useForm<UpdateProposalFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(updateProposalSchema),
  });

  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const companyId = watch("companyId");
  let contactOptions: any = {};

  const confidenceSelectData = CLOSE_CONFIDENCE_OPTIONS.map((v) => ({
    label: `${v}%`,
    value: v,
  }));

  const onUpdate = useCallback(
    async (values: UpdateProposalFormValues) => {
      const result = await updateProposal({
        variables: {
          id: values.id,
          companyId: values.companyId,
          contactId: values.contactId,
          source: values.source,
          ...(values.projectedCloseDate ? { projectedCloseDate: values.projectedCloseDate } : {}),
          ...(values.closeConfidence != null ? { closeConfidence: values.closeConfidence } : {}),
        } as any,
      });
      if (isSuccess(result.data)) setEditable(false);
    },
    [updateProposal, isSuccess]
  );

  const companyName = proposal.company?.name ?? "—";
  const contactName =
    [proposal.contact?.firstName, proposal.contact?.lastName].filter(Boolean).join(" ") || "—";
  const source = proposal.source ? headerCase(proposal.source) : "—";
  const projectedCloseDate = proposal.projectedCloseDate ?? "—";
  const closeConfidence = proposal.closeConfidence != null ? `${proposal.closeConfidence}%` : "—";

  return (
    <UiCard variant="info" sx={{ position: "relative" }}>
      <MDBox display="flex" flexDirection="column" gap="6px" pr={!editable ? "72px" : 0}>
        {!editable && (
          <MDBox
            display="flex"
            justifyContent="flex-end"
            sx={{ position: "absolute", top: "12px", right: "12px" }}
          >
            <UiButton
              uiVariant="outlined"
              onClick={() => setEditable(true)}
              sx={{ boxShadow: "none !important" }}
            >
              Edit
            </UiButton>
          </MDBox>
        )}
        {editable ? (
          <CompanyOptions
            triggerRefetch={triggerRefetch}
            selectedCompany={proposal?.company as Company}
            setTriggerRefetch={setTriggerRefetch}
          >
            {({ companyOptions, companyContactOptions, loading, search, pagination }) => {
              contactOptions = companyContactOptions;
              return (
                <>
                  <MDBox>
                    <UiTypography uiVariant="label-xs" sx={editFieldLabelSx}>
                      Client
                    </UiTypography>
                    <MDBox sx={editDropdownSx}>
                      <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                          <ScrollAsyncAutocomplete
                            {...field}
                            value={companyOptions?.find((x) => x.value === field.value)}
                            options={companyOptions}
                            loading={loading}
                            label=""
                            search={search}
                            onLoadMore={pagination.loadMore}
                            hasMore={pagination.hasNextPage}
                            variant="standard"
                          />
                        )}
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <UiTypography uiVariant="label-xs" sx={editFieldLabelSx}>
                      Contact
                    </UiTypography>
                    <MDBox sx={editDropdownSx}>
                      <Controller
                        name="contactId"
                        control={control}
                        render={({ field }) => (
                          <AutoComplete
                            variant="standard"
                            options={companyId ? contactOptions[companyId] : []}
                            disabled={!companyId}
                            field={field}
                            loading={false}
                            error={null}
                            label=""
                          />
                        )}
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <UiTypography uiVariant="label-xs" sx={editFieldLabelSx}>
                      Source
                    </UiTypography>
                    <MDBox sx={editDropdownSx}>
                      <CustomSelect
                        control={control}
                        name="source"
                        data={enumToValueOptions(ProposalSource)}
                        label=""
                        fullWidth
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <UiTypography uiVariant="label-xs" sx={editFieldLabelSx}>
                      Projected Win
                    </UiTypography>
                    <MDBox sx={editDropdownSx}>
                      <Controller
                        name="projectedCloseDate"
                        control={control}
                        render={({ field }) => (
                          <UiInput
                            type="date"
                            fullWidth
                            value={typeof field.value === "string" ? field.value : ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        )}
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <UiTypography uiVariant="label-xs" sx={editFieldLabelSx}>
                      Close Confidence
                    </UiTypography>
                    <MDBox sx={editDropdownSx}>
                      <CustomSelect
                        control={control}
                        name="closeConfidence"
                        data={confidenceSelectData}
                        label=""
                        fullWidth
                      />
                    </MDBox>
                  </MDBox>
                </>
              );
            }}
          </CompanyOptions>
        ) : (
          <>
            <LabeledValue label="Client" value={companyName} />
            <LabeledValue label="Contact" value={contactName} />
            <LabeledValue label="Source" value={source} />
            <LabeledValue label="Projected Win" value={projectedCloseDate} />
            <LabeledValue label="Close Confidence" value={String(closeConfidence)} />
          </>
        )}

        {editable && (
          <MDBox display="flex" justifyContent="flex-end" gap="8px" mt="8px">
            <UiButton
              uiVariant="primary-red"
              onClick={() => setEditable(false)}
              sx={{ boxShadow: "none !important" }}
            >
              Cancel
            </UiButton>
            <UiButton
              uiVariant="primary-green"
              onClick={handleSubmit(onUpdate)}
              sx={{ boxShadow: "none !important" }}
            >
              Save
            </UiButton>
          </MDBox>
        )}
      </MDBox>
    </UiCard>
  );
}
